import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { userService, UserData } from '../../api/userService';
import { theme } from '../../styles/theme';
import Button from '../../components/UI/Button';

const ProfileContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: ${theme.spacing.lg};
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const AvatarContainer = styled.div`
  flex-shrink: 0;
  margin-right: ${theme.spacing.lg};
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: ${theme.spacing.md};
  }
`;

const Avatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background-color: ${theme.colors.light};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    width: 60px;
    height: 60px;
    color: ${theme.colors.primary};
  }
`;

const UserInfo = styled.div`
  flex: 1;
`;

const DisplayName = styled.h1`
  font-size: ${theme.fontSizes.xxlarge};
  margin-bottom: ${theme.spacing.xs};
`;

const Username = styled.p`
  color: ${theme.colors.gray};
  font-size: ${theme.fontSizes.medium};
  margin-bottom: ${theme.spacing.md};
`;

const UserRole = styled.span`
  display: inline-block;
  background-color: ${theme.colors.primary};
  color: white;
  font-size: ${theme.fontSizes.small};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.radii.small};
  margin-bottom: ${theme.spacing.md};
`;

const ActionButtons = styled.div`
  margin-top: ${theme.spacing.md};
  
  a {
    margin-right: ${theme.spacing.md};
    text-decoration: none;
  }
`;

const ProfileDetailsSection = styled.div`
  background-color: white;
  border-radius: ${theme.radii.medium};
  box-shadow: ${theme.shadows.small};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.large};
  margin-bottom: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.sm};
  border-bottom: 1px solid ${theme.colors.light};
`;

const ProfileDetail = styled.div`
  margin-bottom: ${theme.spacing.md};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  color: ${theme.colors.gray};
  font-size: ${theme.fontSizes.small};
  margin-bottom: ${theme.spacing.xs};
`;

const DetailValue = styled.div`
  font-size: ${theme.fontSizes.medium};
`;

const LoadingContainer = styled.div`
  padding: ${theme.spacing.xl};
  text-align: center;
  font-size: ${theme.fontSizes.large};
`;

const ErrorContainer = styled.div`
  padding: ${theme.spacing.lg};
  background-color: #ffebee;
  color: ${theme.colors.danger};
  border-radius: ${theme.radii.medium};
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
`;

const RetryButton = styled(Button)`
  margin-top: ${theme.spacing.md};
  background-color: ${theme.colors.danger};
  &:hover {
    background-color: ${theme.colors.danger};
    opacity: 0.9;
  }
`;

const ProfileView: React.FC = () => {
  const { user: authUser } = useAuth();
  
  const {
    data: profileResponse,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['userProfile'],
    queryFn: () => userService.getCurrentUser(),
  });
  
  // Access the user data from the response
  // Handle different response structures
  const userData = 
    (profileResponse as any)?.user || 
    (profileResponse as any)?.data?.user || 
    authUser;
  
  if (isLoading) {
    return <LoadingContainer>Loading profile...</LoadingContainer>;
  }
  
  if (error) {
    console.error('Error fetching profile:', error);
    return (
      <ProfileContainer>
        <ErrorContainer>
          Error loading profile. Please try again later.
        </ErrorContainer>
        <RetryButton onClick={() => refetch()}>Retry</RetryButton>
      </ProfileContainer>
    );
  }
  
  return (
    <ProfileContainer>
      <ProfileHeader>
        <AvatarContainer>
          <Avatar>
            {userData?.avatar_url ? (
              <img src={userData.avatar_url} alt={`${userData.display_name || userData.name || userData.username}'s avatar`} />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            )}
          </Avatar>
        </AvatarContainer>
        
        <UserInfo>
          <DisplayName>
            {userData?.display_name || userData?.name || userData?.username || 'User'}
          </DisplayName>
          
          <Username>@{userData?.username || 'username'}</Username>
          
          {userData?.role && (
            <UserRole>{userData.role}</UserRole>
          )}
          
          <ActionButtons>
            <Button as={Link} to="/profile/edit">
              Edit Profile
            </Button>
          </ActionButtons>
        </UserInfo>
      </ProfileHeader>
      
      <ProfileDetailsSection>
        <SectionTitle>Personal Information</SectionTitle>
        
        <ProfileDetail>
          <DetailLabel>Email</DetailLabel>
          <DetailValue>{userData?.email || 'Not provided'}</DetailValue>
        </ProfileDetail>
        
        <ProfileDetail>
          <DetailLabel>Full Name</DetailLabel>
          <DetailValue>
            {userData?.first_name && userData?.last_name
              ? `${userData.first_name} ${userData.last_name}`
              : userData?.name || 'Not provided'}
          </DetailValue>
        </ProfileDetail>
        
        <ProfileDetail>
          <DetailLabel>Bio</DetailLabel>
          <DetailValue>{userData?.bio || 'No bio provided'}</DetailValue>
        </ProfileDetail>
      </ProfileDetailsSection>
      
      <ProfileDetailsSection>
        <SectionTitle>Contact Information</SectionTitle>
        
        <ProfileDetail>
          <DetailLabel>Phone Number</DetailLabel>
          <DetailValue>{userData?.phone_number || 'Not provided'}</DetailValue>
        </ProfileDetail>
        
        <ProfileDetail>
          <DetailLabel>Website</DetailLabel>
          <DetailValue>
            {userData?.website ? (
              <a href={userData.website} target="_blank" rel="noopener noreferrer">
                {userData.website}
              </a>
            ) : (
              'Not provided'
            )}
          </DetailValue>
        </ProfileDetail>
      </ProfileDetailsSection>
      
      <ProfileDetailsSection>
        <SectionTitle>Additional Information</SectionTitle>
        
        <ProfileDetail>
          <DetailLabel>Company</DetailLabel>
          <DetailValue>{userData?.company || 'Not provided'}</DetailValue>
        </ProfileDetail>
        
        <ProfileDetail>
          <DetailLabel>Job Title</DetailLabel>
          <DetailValue>{userData?.job_title || 'Not provided'}</DetailValue>
        </ProfileDetail>
        
        <ProfileDetail>
          <DetailLabel>Member Since</DetailLabel>
          <DetailValue>
            {userData?.created_at
              ? new Date(userData.created_at).toLocaleDateString()
              : 'Unknown'}
          </DetailValue>
        </ProfileDetail>
      </ProfileDetailsSection>
    </ProfileContainer>
  );
};

export default ProfileView; 